// What a printed bill says, worked out once (docs/plans/bill-logo.md): the A4 bill and the
// receipt in print.tsx and their PDFs in bill-pdf.ts all read this, so paper and file never
// say different things. Pure; amounts come out formatted.
import { adToBs, amountInWords, formatMoney, lineDiscount, lineQtyText, lineRate } from '@ims/core'
import type { InvoiceLineRow, InvoiceRow, TenantSettingsRow } from '@ims/data'
import { balanceLine, type BillAdjustments } from './bill-adjustments.ts'
import type { BillService } from './orders.ts'
import { billTitle, registrationOf, showsVat, taxNumberLabel } from './registration.ts'

export { lineDiscount, lineQtyText, lineRate }

export type PrintSettingsLike =
  | Pick<
      TenantSettingsRow,
      | 'business_name'
      | 'legal_name'
      | 'address'
      | 'phone'
      | 'email'
      | 'pan_vat_number'
      | 'tax_registration'
      | 'currency_symbol'
      | 'invoice_footer'
      | 'logo'
      | 'logo_on_receipt'
    >
  | null
  | undefined

export interface BillViewInput {
  invoice: Pick<
    InvoiceRow,
    | 'id'
    | 'number'
    | 'status'
    | 'invoice_date'
    | 'due_date'
    | 'customer_name'
    | 'customer_address'
    | 'customer_pan'
    | 'subtotal_paisa'
    | 'discount_paisa'
    | 'taxable_paisa'
    | 'tax_paisa'
    | 'round_off_paisa'
    | 'total_paisa'
    | 'notes'
  >
  lines: readonly (Pick<
    InvoiceLineRow,
    | 'id'
    | 'line_no'
    | 'description'
    | 'qty_milli'
    | 'unit_price_paisa'
    | 'discount_paisa'
    | 'tax_rate_bp'
    | 'total_paisa'
  > &
    Partial<Pick<InvoiceLineRow, 'gross_paisa' | 'sold_unit' | 'sold_qty_milli' | 'hs_code'>>)[]
  settings: PrintSettingsLike
  balancePaisa: number | null
  /** Batch and serial notes per line id (print.tsx `batchText` / `serialText`). */
  lineNotes?: ReadonlyMap<string, string[]>
  adjustments?: BillAdjustments | null
  copyLabel?: string | null
  service?: BillService | null
  /** Paid at the counter and the change given (quick bill's receipt). */
  paidPaisa?: number
  changePaisa?: number
  /** The sales order the bill came from: its number. */
  reference?: string | null
  /** Who prints it and when (an ISO timestamp). */
  printedBy?: string | null
  printedAt?: string
  /** The api Worker's address, for the QR's VERIFY link (`platform.apiUrl`). */
  apiUrl?: string | null
}

export interface BillRow {
  no: number
  description: string
  hsCode: string
  notes: string[]
  qty: string
  rate: string
  discount: string
  tax: string
  amount: string
}

export interface BillLine {
  label: string
  value: string
  strong?: boolean
}

export interface BillView {
  title: string
  number: string
  copyLabel: string | null
  seller: {
    name: string
    legalName: string | null
    address: string | null
    /** "PAN: 609812345" or "VAT No.: 609876543". */
    taxLine: string | null
    phone: string | null
    email: string | null
    logo: string | null
    logoOnReceipt: boolean
  }
  buyer: { name: string; address: string | null; pan: string | null }
  /** Invoice no., Invoice date, Due date, Reference, Payment. */
  details: BillLine[]
  service: BillService | null
  vat: boolean
  /** Whether any line has a discount (the Disc. column shows only then). */
  discounts: boolean
  rows: BillRow[]
  /** Sub total … Net total (strong). */
  totals: BillLine[]
  /** Under the total: Paid and Balance due, or returns and payments. */
  after: BillLine[]
  /** Credit notes listed under the total (a bill something was returned from). */
  credits: BillLine[]
  words: string
  remarks: string | null
  footer: string | null
  contact: string | null
  qrText: string
  /** "Printed by Sita Sharma 26-06-2083 | 12-10-2026 13:58". */
  printed: string | null
}

const pad = (n: number) => String(n).padStart(2, '0')

/** "12-10-2026" (AD), from an ISO date or timestamp's day. */
export function adDay(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-')
  return `${d}-${m}-${y}`
}

/** "26-06-2083 | 12-10-2026": the Nepali day, then the English one. */
export function dualDay(iso: string): string {
  const day = iso.slice(0, 10)
  try {
    const bs = adToBs(day)
    return `${pad(bs.day)}-${pad(bs.month)}-${bs.year} | ${adDay(day)}`
  } catch {
    return adDay(day)
  }
}

/** A local timestamp's day as YYYY-MM-DD and its time as HH:MM. */
function localParts(iso: string): { day: string; time: string } {
  const d = new Date(iso)
  return {
    day: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  }
}

/** "Printed by Sita Sharma 26-06-2083 | 12-10-2026 13:58", or null without a time. */
export function printedLine(
  by: string | null | undefined,
  at: string | null | undefined,
): string | null {
  if (!at) return null
  const local = localParts(at)
  return `${by ? `Printed by ${by} ` : 'Printed '}${dualDay(local.day)} ${local.time}`
}

/** Paid, Part paid or Unpaid, from what is still owed. */
function paymentWords(total: number, balance: number | null): string | null {
  if (balance === null) return null
  if (balance <= 0) return 'Paid'
  return balance < total ? 'Part paid' : 'Unpaid'
}

/**
 * The text inside a bill's QR: the buyer's PAN (when there is one), the seller's, the
 * number, the day, the totals and a link anyone can open to check the bill is real.
 */
export function billQrText(
  invoice: Pick<
    InvoiceRow,
    'id' | 'number' | 'invoice_date' | 'total_paisa' | 'tax_paisa' | 'customer_pan'
  >,
  settings: PrintSettingsLike,
  apiUrl: string | null | undefined,
): string {
  const vat = showsVat(registrationOf(settings), invoice.tax_paisa)
  const lines = [
    invoice.customer_pan?.trim() && `BUYER PAN : ${invoice.customer_pan.trim()}`,
    settings?.pan_vat_number && `SELLER PAN : ${settings.pan_vat_number}`,
    `INVOICE NO. : ${invoice.number ?? 'Number assigned on sync'}`,
    `INVOICE DATE : ${adDay(invoice.invoice_date)}`,
    `BILL TOTAL : ${formatMoney(invoice.total_paisa)}`,
    vat && `VAT TOTAL : ${formatMoney(invoice.tax_paisa)}`,
    apiUrl && `VERIFY : ${apiUrl.replace(/\/$/, '')}/v/${invoice.id}`,
  ]
  return lines.filter(Boolean).join('\n')
}

export function billView(input: BillViewInput): BillView {
  const { invoice, settings } = input
  const m = (p: number) => formatMoney(p, { symbol: settings?.currency_symbol ?? 'Rs.' })
  const plain = (p: number) => formatMoney(p)
  const registration = registrationOf(settings)
  const vat = showsVat(registration, invoice.tax_paisa)
  const number =
    invoice.number ?? (invoice.status === 'draft' ? 'Draft' : 'Number assigned on sync')

  const details: BillLine[] = [
    { label: 'Invoice no.', value: number },
    { label: 'Invoice date', value: dualDay(invoice.invoice_date) },
  ]
  if (invoice.due_date) details.push({ label: 'Due date', value: dualDay(invoice.due_date) })
  if (input.reference) details.push({ label: 'Reference', value: input.reference })
  const paid = paymentWords(invoice.total_paisa, input.balancePaisa)
  if (paid && invoice.status === 'finalized') details.push({ label: 'Payment', value: paid })

  const rows: BillRow[] = input.lines.map((l) => ({
    no: l.line_no,
    description: l.description,
    hsCode: l.hs_code ?? '',
    notes: input.lineNotes?.get(l.id) ?? [],
    qty: lineQtyText(l),
    rate: plain(lineRate(l)),
    discount: lineDiscount(l) ? plain(lineDiscount(l)) : '',
    tax: vat ? (l.tax_rate_bp ? `${l.tax_rate_bp / 100}% VAT` : 'Exempt') : '',
    amount: plain(l.total_paisa),
  }))

  const totals: BillLine[] = [{ label: 'Sub total', value: plain(invoice.subtotal_paisa) }]
  if (invoice.discount_paisa > 0)
    totals.push({ label: 'Discount', value: `−${plain(invoice.discount_paisa)}` })
  if (vat) {
    totals.push({ label: 'Taxable amount', value: plain(invoice.taxable_paisa) })
    totals.push({ label: 'VAT', value: plain(invoice.tax_paisa) })
  }
  if (invoice.round_off_paisa !== 0)
    totals.push({ label: 'Round off', value: plain(invoice.round_off_paisa) })
  totals.push({ label: 'Net total', value: m(invoice.total_paisa), strong: true })

  const after: BillLine[] = []
  const credits: BillLine[] = []
  if (input.adjustments) {
    for (const c of input.adjustments.credits)
      credits.push({
        label: `Credit note ${c.number ?? '(number on sync)'} (${dualDay(c.note_date)})`,
        value: `−${m(c.total_paisa)}`,
      })
    after.push({ label: 'Paid', value: m(input.adjustments.paidPaisa) })
    after.push({
      label: balanceLine(input.adjustments.balancePaisa),
      value: m(Math.abs(input.adjustments.balancePaisa)),
      strong: true,
    })
  } else {
    if (input.paidPaisa !== undefined) after.push({ label: 'Paid', value: m(input.paidPaisa) })
    else if (input.balancePaisa !== null && invoice.status === 'finalized')
      after.push({ label: 'Paid', value: m(invoice.total_paisa - Math.max(0, input.balancePaisa)) })
    if (input.changePaisa !== undefined && input.changePaisa > 0)
      after.push({ label: 'Change', value: m(input.changePaisa) })
    if (input.balancePaisa !== null && input.balancePaisa > 0)
      after.push({ label: 'Balance due', value: m(input.balancePaisa) })
  }

  const contactBits = [
    settings?.phone && `call ${settings.phone}`,
    settings?.email && `write to ${settings.email}`,
  ]
  const contact = contactBits.some(Boolean)
    ? `For any questions about this bill, ${contactBits.filter(Boolean).join(' or ')}.`
    : null
  const printed = printedLine(input.printedBy, input.printedAt)

  return {
    title: billTitle(registration, invoice.status),
    number,
    copyLabel: input.copyLabel ?? null,
    seller: {
      name: settings?.business_name ?? '',
      legalName: settings?.legal_name ?? null,
      address: settings?.address ?? null,
      taxLine: settings?.pan_vat_number
        ? `${taxNumberLabel(registration)}: ${settings.pan_vat_number}`
        : null,
      phone: settings?.phone ?? null,
      email: settings?.email ?? null,
      logo: settings?.logo ?? null,
      logoOnReceipt: settings?.logo_on_receipt ?? true,
    },
    buyer: {
      name: invoice.customer_name || 'Walk-in customer',
      address: invoice.customer_address,
      pan: invoice.customer_pan,
    },
    details,
    service: input.service ?? null,
    vat,
    discounts: input.lines.some((l) => lineDiscount(l) > 0),
    rows,
    totals,
    after,
    credits,
    words: amountInWords(invoice.total_paisa),
    remarks: invoice.notes,
    footer: settings?.invoice_footer ?? null,
    contact,
    qrText: billQrText(invoice, settings, input.apiUrl),
    printed,
  }
}
